/**
 * Static suggestion catalog + context-aware selector for <BaselinxCopilot>.
 *
 * Kept as a pure data module (no JSX, no React hooks) so it can be imported
 * from unit tests and from multiple copilot sub-components without incurring
 * any render cost. Changes to the suggestion taxonomy belong here.
 */
import {
  HelpCircle,
  Navigation,
  Search,
  BarChart3,
} from 'lucide-react';

export const defaultSuggestions = [
  { text: 'What can you help me with?', icon: HelpCircle, category: 'general' },
  { text: 'Show me the dashboard overview', icon: BarChart3, category: 'navigation' },
  { text: 'How do I create an ECO?', icon: Navigation, category: 'workflow' },
  { text: 'Search for parts', icon: Search, category: 'search' },
];

export const suggestionCategories = {
  navigation: [
    { text: 'Take me to Change Control', icon: Navigation },
    { text: 'Open the Parts tab', icon: Navigation },
    { text: 'Show BOMs management', icon: Navigation },
    { text: 'Go to Quality dashboard', icon: Navigation },
    { text: 'Open CAD System', icon: Navigation },
    { text: 'View Audit Log', icon: Navigation },
  ],
  ecoCycle: [
    { text: 'How do I create a new ECO?', icon: HelpCircle },
    { text: "What's the ECO approval workflow?", icon: HelpCircle },
    { text: 'Explain Class I vs Class II changes', icon: HelpCircle },
    { text: 'How do I route an ECO for approval?', icon: HelpCircle },
    { text: 'What triggers a CCB review?', icon: HelpCircle },
    { text: 'How do I link parts to an ECO?', icon: HelpCircle },
  ],
  partsAndBoms: [
    { text: 'How do I add a new part?', icon: HelpCircle },
    { text: 'Search parts by material type', icon: Search },
    { text: 'Show me released parts', icon: Search },
    { text: 'How do I create a BOM?', icon: HelpCircle },
    { text: 'Explain part revision control', icon: HelpCircle },
    { text: 'What is configuration management?', icon: HelpCircle },
  ],
  compliance: [
    { text: 'What is NIST 800-171?', icon: HelpCircle },
    { text: 'Show me our Cyber Policy', icon: Navigation },
    { text: 'How do we handle CUI data?', icon: HelpCircle },
    { text: 'Explain DFARS compliance', icon: HelpCircle },
    { text: "What's our data handling policy?", icon: HelpCircle },
    { text: 'View audit logs for compliance', icon: Navigation },
  ],
  integrations: [
    { text: 'How does CAD integration work?', icon: HelpCircle },
    { text: 'Connect NexCAD to create ECOs', icon: HelpCircle },
    { text: 'What systems integrate with Baselinx?', icon: HelpCircle },
    { text: 'How do I sync with Altium?', icon: HelpCircle },
    { text: 'ERP integration options', icon: HelpCircle },
  ],
  reports: [
    { text: 'Show me metrics dashboard', icon: BarChart3 },
    { text: 'Generate ECO status report', icon: BarChart3 },
    { text: 'View quality metrics', icon: BarChart3 },
    { text: 'Part revision history', icon: Search },
    { text: 'Export audit trail', icon: BarChart3 },
  ],
  website: [
    { text: 'What industries does Baselinx serve?', icon: HelpCircle },
    { text: 'Tell me about pricing plans', icon: HelpCircle },
    { text: 'How do I request a demo?', icon: HelpCircle },
    { text: 'What makes Baselinx different?', icon: HelpCircle },
    { text: 'Contact support', icon: HelpCircle },
  ],
};

/**
 * Pick the right suggestion bundle for the page the user is looking at.
 * @param {{ page: string, activeTab: string }} ctx current route + tab
 * @returns array of suggestions with {text, icon}
 */
export function getContextSuggestions(ctx) {
  const { page, activeTab } = ctx;

  if (page.includes('/dashboard') || page.includes('/app')) {
    switch (activeTab) {
      case 'changecontrol':
        return suggestionCategories.ecoCycle.slice(0, 4);
      case 'parts':
        return suggestionCategories.partsAndBoms.slice(0, 4);
      case 'boms':
        return suggestionCategories.partsAndBoms.slice(2, 6);
      case 'quality':
      case 'audits':
        return suggestionCategories.compliance.slice(0, 4);
      case 'cadsystem':
        return suggestionCategories.integrations.slice(0, 4);
      case 'metrics':
        return suggestionCategories.reports.slice(0, 4);
      default:
        return defaultSuggestions;
    }
  }

  if (page === '/' || page.includes('/features') || page.includes('/pricing')) {
    return suggestionCategories.website.slice(0, 4);
  }

  return defaultSuggestions;
}
